<template>
  <div
    class="relative flex-col flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full p-3 cursor-pointer conversation border-b border-b-neutral-100 ltr:border-l-2 rtl:border-r-2"
    :class="{
      'active bg-[#F5F5F5]': isActiveChat,
      'border-transparent': !isActiveChat,
      'unread-chat': hasUnread || manualUnread,
      'conversation-selected': selected,
      'ltr:border-l-green-300 rtl:border-r-green-300': subconversationOpened,
      'ltr:border-l-transparent rtl:border-l-transparent':
        !subconversationOpened,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div class="flex flex-row w-full mb-1 gap-1">
      <div class="flex flex-row gap-2 flex-1">
        <div v-if="isAnEmailChannel && chat.messages.length">
          <label v-if="showCheckbox" class="checkbox-wrapper" @click.stop>
            <input
              :value="selected"
              :checked="selected"
              class="semantic-checkbox"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
          <button
            v-else
            class="self-center h-[32px]"
            @click="toggleSubsconversation"
          >
            <span
              v-if="!subconversationOpened"
              class="material-symbols-outlined expand-email-submessage rtl:rotate-180"
            >
              keyboard_arrow_right
            </span>
            <span
              v-else
              class="material-symbols-outlined expand-email-submessage"
            >
              keyboard_arrow_down
            </span>
          </button>
        </div>
        <label v-else-if="showCheckbox" class="checkbox-wrapper" @click.stop>
          <input
            :value="selected"
            :checked="selected"
            class="semantic-checkbox"
            type="checkbox"
            @change="onSelectConversation($event.target.checked)"
          />
        </label>
        <thumbnail
          :src="currentContact.thumbnail"
          class="columns no-margin my-[3px]"
          :username="currentContact.name"
          :status="currentContact.availability_status"
          :badge="inboxBadge"
          :enabled-status-indicator="!isAnEmailChannel"
          size="32px"
        />
        <div class="flex flex-col flex-1">
          <div class="flex items-center">
            <inbox-name
              v-if="inbox"
              :inbox="inbox"
              :mode="inboxNameMode"
              :current-contact="currentContact"
            />
          </div>
          <div
            class="text-sm/[20px] font-bold text-gray-800 flex flex-row gap-[5px]"
          >
            <span>{{ currentContact.name }}</span>
          </div>
        </div>
      </div>
      <div class="flex flex-row items-center gap-1">
        <SLA-card-label
          v-if="isDisplaySlaLabel"
          :chat="chat"
          class="ltr:mr-1 rtl:ml-1"
        />
        <div
          v-if="isSnoozed"
          v-tooltip.top="snoozedTooltip"
          class="rounded-full border border-gray-700 px-[7px] py-[2px] flex ltr:flex-row rtl:flex-row-reverse items-center justify-center gap-1 mr-1 rtl:mr-0 rtl-ml-1"
        >
          <img :src="snoozeIcon" class="w-3 h-3 mb-0.5" />
          <span v-if="snoozedDiffTime" class="text-xxs text-gray-700">
            {{ snoozedDiffTime }}
          </span>
        </div>
        <priority-mark :priority="chat.priority" />
        <div>
          <img
            v-if="isBot"
            :src="botIcon"
            class="w-7 h-7 p-[6px] border border-solid border-gray-200 rounded-[4px] ltr:ml-[2px] rtl:mr-[2px] bg-white"
            style="box-shadow: 0 1px 8px 0 rgba(221, 221, 221, 0.25)"
            @contextmenu.stop="openStatusContextMenu($event)"
          />
          <img
            v-else
            :src="agentIcon"
            class="w-7 h-7 p-[6px] border border-solid border-gray-200 rounded-[4px] ltr:ml-[2px] rtl:mr-[2px] bg-white"
            style="box-shadow: 0 1px 8px 0 rgba(221, 221, 221, 0.25)"
            @contextmenu.stop="openStatusContextMenu($event)"
          />
        </div>
        <span
          v-if="showUnreadIndicator"
          class="rounded-full text-xxs font-bold w-[18px] h-[18px] flex flex-center text-center text-white bg-green-500 ltr:ml-1 rtl:mr-1"
        >
          {{ unreadBadgeText }}
        </span>
      </div>
    </div>
    <div class="flex flex-row items-start justify-between w-full gap-3">
      <message-preview
        v-if="lastMessageInChat"
        :message="lastMessageInChat"
        :additional-attributes="chat.additional_attributes"
        class="conversation--message my-0 max-w-[96%] h-[26px] overflow-hidden text-ellipsis text-gray-600 dark:text-slate-200"
      />
      <p
        v-else
        class="conversation--message flex items-center gap-1 my-0 text-xs/[16px] max-w-[96%] overflow-hidden text-ellipsis text-gray-600 dark:text-slate-200"
      >
        <span
          class="material-symbols-outlined text-base/[16px] text-gray-500 ltr:ml-[-2px] rtl:mr-[-2px]"
        >
          info
        </span>
        <span class="text-xs/[26px] text-gray-500">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        v-if="assignee.name || team.name"
        class="flex flex-col items-start border border-gray-100 rounded-[4px] gap-1 px-1.5 py-1"
      >
        <div v-if="assignee.name" class="flex flex-row item-center gap-[3px]">
          <span class="material-symbols-outlined text-[16px] text-gray-500">
            person
          </span>
          <span class="text-xs/[18px] text-gray-700 whitespace-nowrap">
            {{ assignee.name }}
          </span>
        </div>
        <div v-if="team.name" class="flex flex-row item-center gap-[3px]">
          <span class="material-symbols-outlined text-[16px] text-gray-500">
            groups
          </span>
          <span class="text-xs/[18px] text-gray-700 whitespace-nowrap">
            {{ team.name }}
          </span>
        </div>
      </div>
    </div>
    <div class="flex flex-row items-center justify-between w-full mt-2">
      <div>
        <card-labels
          :conversation-id="chat.id"
          class="flex flex-row items-center w-full flex-1"
        />
      </div>
      <div class="flex flex-row items-center gap-1">
        <div class="text-xs text-gray-500">
          {{ messageStampFull(chat.timestamp) }}
        </div>
        <span v-if="pinned">
          <fluent-icon
            icon="pin"
            size="14"
            class="text-slate-500 dark:text-slate-400"
          />
        </span>
      </div>
    </div>
    <div v-if="subconversationOpened" class="flex flex-col w-full pt-3">
      <div
        v-for="message in chatMessages"
        :key="message.id"
        class="py-2 last:mb-0 border-t border-neutral-200 last:pb-0 relative dark:border-slate-800/75 columns"
        @click.prevent.stop="handlePreviewClick(message)"
      >
        <div class="flex flex-row justify-between items-center mb-1">
          <div class="flex flex-col flex-1">
            <div class="flex items-center">
              <inbox-name
                v-if="inbox"
                :inbox="inbox"
                :mode="inboxNameMode"
                :current-contact="message.sender"
              />
            </div>
            <div
              class="text-sm/[20px] font-bold text-gray-800 flex flex-row gap-[5px]"
            >
              <span>{{ message.sender.name }}</span>
            </div>
          </div>
          <div class="self-center flex items-center gap-1">
            <span
              v-if="() => isMessagePrivate(message)"
              class="material-symbols-outlined text-gray-600 text-base/[16px] -mt-0.5 cursor-pointer"
            >
              lock
            </span>
            <span
              v-else-if="() => messageByAgent(message)"
              class="material-symbols-outlined text-gray-600 text-base/[16px] -mt-0.5 cursor-pointer"
            >
              reply
            </span>
            <span
              v-else-if="() => isMessageAnActivity(message)"
              class="material-symbols-outlined text-gray-600 text-base/[16px] -mt-0.5 cursor-pointer"
            >
              info
            </span>
            <span
              class="material-symbols-outlined text-gray-600 text-base/[16px] -mt-0.5 cursor-pointer"
              @click="() => openDeleteModal(message)"
            >
              delete
            </span>
          </div>
        </div>
        <div class="flex flex-row items-center">
          <email-message-preview
            :message="message"
            :additional-attributes="chat.additional_attributes"
            :enable-delete="true"
            class="conversation--message my-0 leading-6 h-6 text-xs text-slate-700 dark:text-slate-200"
          />
          <div class="flex flex-col conversation--meta min-w-[80px]">
            <span
              dir="ltr"
              class="ml-auto font-normal leading-4 text-black-600 text-xxs"
            >
              {{ messageStampFull(message.created_at) }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <woot-context-menu
      v-if="!hideContextMenu && showContextMenu"
      ref="menu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <conversation-context-menu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :has-unread-messages="
          unreadCount > 0 || chat.has_incoming_messages == 0
        "
        :conversation-id="chat.id"
        :pinned="pinned"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @assign-priority="assignPriority"
        @pin="onPin"
      />
    </woot-context-menu>
    <woot-context-menu
      v-if="enabledStatusContextMenu && showStatusContextMenu"
      ref="menu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeStatusContextMenu"
    >
      <status-context-menu
        :is-bot="isBot"
        @update-conversation="onUpdateConversation"
        @close="closeStatusContextMenu"
      />
    </woot-context-menu>
    <woot-delete-modal
      v-if="showDeleteModal"
      class="context-menu--delete-modal"
      :show.sync="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import wootConstants from 'dashboard/constants/globals';
import router from 'dashboard/routes';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { conversationReopenTime } from 'dashboard/helper/snoozeHelpers';

import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import MessagePreview from 'dashboard/components/widgets/conversation/MessagePreview.vue';
import EmailMessagePreview from 'dashboard/components/widgets/conversation/EmailMessagePreview.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import ConversationContextMenu from 'dashboard/components/widgets/conversation/contextMenu/Index.vue';
import CardLabels from 'dashboard/components/widgets/conversation/conversationCardComponents/CardLabelsV2.vue';
import PriorityMark from 'dashboard/components/widgets/conversation/PriorityMark.vue';
import SLACardLabel from 'dashboard/components/widgets/conversation/components/SLACardLabel.vue';
import StatusContextMenu from './StatusContextMenu.vue';

import workingHoursMixin from 'shared/mixins/workingHoursMixin';
import conversationMixin from 'dashboard/mixins/conversations';
import timeMixin from 'dashboard/mixins/time';
import localeMixin from 'shared/mixins/localeMixin';
import inboxMixin from 'shared/mixins/inboxMixin';
import alertMixin from 'shared/mixins/alertMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

import agentIcon from 'dashboard/assets/images/agent.svg';
import botIcon from 'dashboard/assets/images/bot.svg';
import snoozeIcon from 'dashboard/assets/images/snooze.svg';
import { INBOX_NAME_MODES } from 'shared/mixins/inboxMixin';
import { MESSAGE_TYPE } from 'widget/helpers/constants';

export default {
  components: {
    CardLabels,
    InboxName,
    Thumbnail,
    ConversationContextMenu,
    MessagePreview,
    PriorityMark,
    SLACardLabel,
    EmailMessagePreview,
    StatusContextMenu,
  },
  mixins: [
    inboxMixin,
    timeMixin,
    conversationMixin,
    alertMixin,
    localeMixin,
    workingHoursMixin,
    hasPermissionMixin,
  ],
  props: {
    pinned: {
      type: Boolean,
      default: false,
    },
    activeLabel: {
      type: String,
      default: '',
    },
    chat: {
      type: Object,
      default: () => {},
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showAssignee: {
      type: Boolean,
      default: false,
    },
    conversationType: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
      default: false,
    },
    hideContextMenu: {
      type: Boolean,
      default: false,
    },
    disableHoverCheckbox: {
      type: Boolean,
      default: false,
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeSubconversationId: null,
      openSubconversation: false,
      showContextMenu: false,
      showStatusContextMenu: false,
      contextMenu: {
        x: null,
        y: null,
      },
      recentlyMarkedAsUnread: false,
      agentIcon,
      botIcon,
      snoozeIcon,
      showDeleteModal: false,
      messageSelected: null,
    };
  },
  computed: {
    subconversationOpened() {
      return this.isActiveChat && this.openSubconversation;
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxesList: 'inboxes/getInboxes',
      activeInbox: 'getSelectedInbox',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      itemMarkedAsUnread: 'getItemMarkedAsUnread',
    }),
    chatMessages() {
      return this.chat.messages
        .filter(message => {
          return !message.private && message.sender;
        })
        .sort((a, b) => b.created_at - a.created_at);
    },
    chatMetadata() {
      return this.chat.meta || {};
    },
    assignee() {
      return this.chatMetadata.assignee || {};
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chatMetadata.sender.id
      );
    },
    isActiveChat() {
      return this.currentChat.id === this.chat.id;
    },
    unreadCount() {
      return this.chat.unread_count;
    },
    hasUnread() {
      return this.unreadCount > 0 || this.recentlyMarkedAsUnread;
    },
    manualUnread() {
      return this.recentlyMarkedAsUnread || this.isManualUnread(this.chat);
    },
    lastMessageInChat() {
      return this.lastMessage(this.chat);
    },
    inbox() {
      const { inbox_id: inboxId } = this.chat;
      const stateInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return stateInbox;
    },
    inboxName() {
      const stateInbox = this.inbox;
      return stateInbox.name || '';
    },
    hasSlaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    // todo copypast
    isOpen() {
      return this.chat.status === wootConstants.STATUS_TYPE.OPEN;
    },
    isResolved() {
      return this.chat.status === wootConstants.STATUS_TYPE.RESOLVED;
    },
    isBot() {
      return this.isOpen || this.isResolved;
    },
    isSnoozed() {
      return this.chat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    snoozedTooltip() {
      const { snoozed_until: snoozedUntil } = this.chat;
      if (snoozedUntil) {
        return `${this.$t(
          'CONVERSATION.HEADER.SNOOZED_UNTIL'
        )} ${conversationReopenTime(snoozedUntil, this.$i18n.locale)}`;
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    },
    appliedSLA() {
      return this.chat?.applied_sla;
    },
    isSlaWorkingHours() {
      return (
        !this.appliedSLA?.sla_only_during_business_hours ||
        this.isInBusinessHours
      );
    },
    isDisplaySlaLabel() {
      return !this.isResolved && this.hasSlaPolicyId && this.isSlaWorkingHours;
    },
    team() {
      return this.chatMetadata.team || {};
    },
    snoozedDiffTime() {
      const { snoozed_until: snoozedUntil } = this.chat;
      if (!snoozedUntil) return null;
      const diffMs = Number(snoozedUntil) - Date.now();
      if (!Number.isFinite(diffMs) || diffMs <= 0) return null;
      const diffMin = Math.ceil(diffMs / 60000);
      if (diffMin < 60) return `${diffMin}m`;
      const diffHour = Math.floor(diffMin / 60);
      if (diffHour < 24) return `${diffHour}h`;
      return `${Math.floor(diffHour / 24)}d`;
    },
    unreadBadgeText() {
      if (this.manualUnread) return '';
      return this.unreadCount > 9 ? '9+' : this.unreadCount;
    },
    // manual unread shows the circle without a counter, so the indicator
    // has to be rendered even when unreadBadgeText is empty
    showUnreadIndicator() {
      return this.manualUnread || Boolean(this.unreadBadgeText);
    },
    isMessageAnActivity(message) {
      const { message_type: messageType } = message;
      return messageType === MESSAGE_TYPE.ACTIVITY;
    },
    messageByAgent(message) {
      const { message_type: messageType } = message;
      return messageType === MESSAGE_TYPE.OUTGOING;
    },
    isMessagePrivate(message) {
      const { private: isPrivate } = message;
      return isPrivate;
    },
    inboxNameMode() {
      if (this.isAnEmailChannel) return INBOX_NAME_MODES.EMAIL_ADDRESS;
      return INBOX_NAME_MODES.INBOX_NAME;
    },
    hasSwitchConversationStatus() {
      const { inbox_id: inboxId } = this.chat;
      return this.hasInboxPermission('Inbox manual/bot switch', inboxId);
    },
    enabledStatusContextMenu() {
      if (this.hideContextMenu) return false;
      if (!this.hasSwitchConversationStatus) return false;

      return true;
    },
  },
  watch: {
    isActiveChat(newState) {
      if (newState === true) {
        this.recentlyMarkedAsUnread = false;

        if (this.itemMarkedAsUnread === this.chat.id) {
          this.$store.dispatch('setItemMarkedAsUnread', null);
        }
      }
    },
    itemMarkedAsUnread(newVal) {
      this.recentlyMarkedAsUnread = newVal === this.chat.id;
    },
  },
  methods: {
    isActive(message) {
      return this.isActiveChat && this.activeSubconversationId === message.id;
    },
    async handlePreviewClick(message) {
      if (message.id === this.activeSubconversationId) return;
      bus.$emit(BUS_EVENTS.CLOSE_REPLY);
      this.activeSubconversationId = message.id;
      await this.$store.dispatch('showBefore', {
        conversationId: this.currentChat.id,
        before: message.id,
      });
    },
    toggleSubsconversation() {
      this.openSubconversation = !this.openSubconversation;
    },
    onCardClick(e) {
      const { activeInbox, chat } = this;
      const path = frontendURL(
        conversationUrl({
          accountId: this.accountId,
          activeInbox,
          id: chat.id,
          label: this.activeLabel,
          teamId: this.teamId,
          foldersId: this.foldersId,
          conversationType: this.conversationType,
        })
      );

      if (e.metaKey || e.ctrlKey) {
        window.open(
          window.chatwootConfig.hostURL + path,
          '_blank',
          'noopener noreferrer nofollow'
        );
        return;
      }
      if (this.isActiveChat) {
        this.$store.dispatch('clearBeforeConversation');
        return;
      }

      router.push({ path });
    },
    onSelectConversation(checked) {
      const action = checked ? 'select-conversation' : 'de-select-conversation';
      this.$emit(action, this.chat.id, this.inbox.id);
    },
    openContextMenu(e) {
      e.preventDefault();
      if (this.disableHoverCheckbox) return;
      this.$emit('context-menu-toggle', true);
      this.contextMenu.x = e.pageX || e.clientX;
      this.contextMenu.y = e.pageY || e.clientY;
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.$emit('context-menu-toggle', false);
      this.showContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    openStatusContextMenu(e) {
      e.preventDefault();
      if (!this.enabledStatusContextMenu) return;
      if (this.disableHoverCheckbox) return;
      this.$emit('context-menu-toggle', true);
      this.contextMenu.x = (e.pageX || e.clientX) + 60;
      this.contextMenu.y = (e.pageY || e.clientY) + 4;
      this.showStatusContextMenu = true;
    },
    closeStatusContextMenu() {
      this.$emit('context-menu-toggle', false);
      this.showStatusContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    onUpdateConversation(status, snoozedUntil) {
      this.$emit(
        'update-conversation-status',
        this.chat.id,
        status,
        snoozedUntil
      );
    },
    openDeleteModal(message) {
      this.messageSelected = message;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.messageSelected = null;
      this.showDeleteModal = false;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.messageSelected.conversation_id,
          messageId: this.messageSelected.id,
        });
        this.showAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      } finally {
        this.closeDeleteModal();
      }
    },
    async onAssignAgent(agent) {
      this.$emit('assign-agent', agent, [this.chat.id]);
    },
    async onAssignLabel(label) {
      this.$emit('assign-label', [label.title], [this.chat.id]);
    },
    async onAssignTeam(team) {
      this.$emit('assign-team', team, this.chat.id);
    },
    async markAsUnread() {
      this.$emit('mark-as-unread', this.chat.id);
    },
    async assignPriority(priority) {
      this.$emit('assign-priority', priority, this.chat.id);
    },
    async onPin() {
      this.$emit('pin', this.chat.id);
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation {
  &:hover {
    background: #f5f5f5;
  }

  &.unread-chat {
    .unread {
      @apply block;
    }
    .conversation--message {
      @apply font-semibold text-gray-800;

      ::v-deep {
        .message-content {
          @apply text-gray-800;
        }
      }
    }
    .conversation--user {
      @apply font-semibold;
    }
  }

  &.compact {
    @apply pl-0;
    .conversation--details {
      @apply rounded-sm ml-0 pl-5 pr-2;
    }
  }

  &::v-deep .user-thumbnail-box:not(.no-margin) {
    @apply mt-4;
  }

  &.conversation-selected {
    @apply bg-[#F0FDF4] dark:bg-slate-800;
  }

  .checkbox-wrapper {
    @apply h-8 w-8 flex items-center justify-center rounded-full cursor-pointer;

    input[type='checkbox'] {
      @apply m-0 cursor-pointer;
    }
  }
  .active {
    @apply bg-green-50;
  }
}

.mode-icon {
  height: 24px;
  width: 24px;
  margin-inline-start: var(--space-small);
  margin-inline-end: var(--space-small);
}
.mode-noread-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 5rem;
  padding-inline-start: 16px;
}

.expand-email-submessage {
  @apply cursor-pointer text-gray-600 border border-transparent rounded-[4px];
  @apply hover:border-gray-200 hover:bg-gray-50;
}

[dir='rtl'] .conversation--metadata {
  padding-right: unset;
  padding-left: var(--space-normal);
}
</style>
